import { useTranslation } from 'react-i18next';
import { Badge, Loader } from '@mantine/core';
import { STATUS } from '@/lib/statusColors';
import type { ExchangeUiState } from '@/lib/exchangeOffer';

interface ExchangeStatusBadgeProps {
  /** Биржевое UI-состояние оффера; `null` — оффер внебиржевой (показываем «Внебиржевое»). */
  readonly state: ExchangeUiState | null;
  /** Биржевой статус ещё грузится — не выдаём преждевременно «Не на бирже». */
  readonly loading?: boolean;
  readonly size?: string;
}

// Бейдж биржевого присутствия оффера (текст + цвет, не только цвет — §9). Для внебиржевого — нейтральный
// «Внебиржевое». Цвета — из семантических статус-токенов (brand/lime/wheat/red/gray). Пока статус биржи
// грузится — нейтральный бейдж с индикатором вместо возможного ложного «Не на бирже».
export default function ExchangeStatusBadge({ state, loading, size }: ExchangeStatusBadgeProps) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Badge variant="light" color="gray" tt="none" fw={500} radius="sm" size={size} leftSection={<Loader size={10} color="gray" />}>
        {t('app.sell.exchange.loading')}
      </Badge>
    );
  }
  const map: Record<ExchangeUiState, { color: string; key: string }> = {
    ON_EXCHANGE: { color: STATUS.done, key: 'app.sell.exchange.onExchange' },
    OFF_EXCHANGE: { color: STATUS.neutral, key: 'app.sell.exchange.offExchange' },
    FAILED: { color: STATUS.danger, key: 'app.sell.exchange.failed' },
    MATCHED: { color: STATUS.active, key: 'app.sell.exchange.matched' },
  };
  const entry = state ? map[state] : null;
  const label = entry ? t(entry.key) : t('app.sell.notExchange');
  return (
    <Badge
      variant="light"
      color={entry?.color ?? 'gray'}
      tt="none"
      fw={500}
      radius="sm"
      size={size}
    >
      {label}
    </Badge>
  );
}
